import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, User, UserCheck, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { appointmentsApi, patientsApi, doctorsApi, Patient, Doctor } from '../../lib/api';

interface CreateAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  preSelectedPatientId?: string;
  preSelectedDoctorId?: string;
}

const CreateAppointmentModal: React.FC<CreateAppointmentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  preSelectedPatientId,
  preSelectedDoctorId
}) => {
  const [formData, setFormData] = useState({
    patientId: preSelectedPatientId || '',
    doctorId: preSelectedDoctorId || '',
    appointmentDate: '',
    appointmentTime: '',
    notes: ''
  });

  const [patients, setPatients] = useState<Patient[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [availabilityStatus, setAvailabilityStatus] = useState<'checking' | 'available' | 'unavailable' | null>(null);
  const [bookedTimes, setBookedTimes] = useState<string[]>([]);
  const [availableTimes, setAvailableTimes] = useState<string[]>([]);

  // Time slots for appointments
  const timeSlots = [
    '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30',
    '16:00', '16:30', '17:00', '17:30', '18:00'
  ];

  useEffect(() => {
    if (isOpen) {
      fetchInitialData();
      setFormData({
        patientId: preSelectedPatientId || '',
        doctorId: preSelectedDoctorId || '',
        appointmentDate: '',
        appointmentTime: '',
        notes: ''
      });
      setErrors({});
      setAvailabilityStatus(null);
      setBookedTimes([]);
      setAvailableTimes([]);
    }
  }, [isOpen, preSelectedPatientId, preSelectedDoctorId]);

  // Check availability when doctor and date change
  useEffect(() => {
    if (formData.doctorId && formData.appointmentDate) {
      checkDoctorAvailability();
    } else {
      setAvailabilityStatus(null);
      setBookedTimes([]);
      setAvailableTimes([]);
    }
  }, [formData.doctorId, formData.appointmentDate]);

  const fetchInitialData = async () => {
    try {
      setLoadingData(true);
      const [patientsResponse, doctorsResponse] = await Promise.all([
        patientsApi.getPatients({ limit: 100 }),
        doctorsApi.getDoctors({ limit: 100 })
      ]);

      if (patientsResponse.data.success && patientsResponse.data.data) {
        setPatients(patientsResponse.data.data.patients);
      }

      if (doctorsResponse.data.success && doctorsResponse.data.data) {
        setDoctors(doctorsResponse.data.data.doctors);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load patients and doctors');
    } finally {
      setLoadingData(false);
    }
  };

  const checkDoctorAvailability = async () => {
    try {
      setAvailabilityStatus('checking');
      const response = await appointmentsApi.getDoctorAvailability(
        formData.doctorId,
        formData.appointmentDate
      );

      if (response.data.success && response.data.data) {
        // Check if there's a message about no schedule
        const message = response.data.data?.message;
        if (message) {
          setAvailabilityStatus('unavailable');
          setErrors(prev => ({
            ...prev,
            appointmentTime: message || 'No schedule available for this day'
          }));
          setBookedTimes([]);
          setAvailableTimes([]);
          return;
        }

        setBookedTimes(response.data.data.bookedTimes);
        setAvailableTimes(response.data.data.availableTimes);
        
        // If no available times, set status to unavailable
        if (response.data.data.availableTimes.length === 0) {
          setAvailabilityStatus('unavailable');
          setErrors(prev => ({
            ...prev,
            appointmentTime: 'Doctor has no available time slots on this date'
          }));
        } else {
          setAvailabilityStatus('available');
          // Clear any previous error
          if (errors.appointmentTime) {
            setErrors(prev => {
              const { appointmentTime, ...rest } = prev;
              return rest;
            });
          }
        }
      }
    } catch (error) {
      console.error('Error checking availability:', error);
      setAvailabilityStatus('unavailable');
      setErrors(prev => ({
        ...prev,
        appointmentTime: 'Failed to check doctor availability. Please try again.'
      }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.patientId) {
      newErrors.patientId = 'Please select a patient';
    }

    if (!formData.doctorId) {
      newErrors.doctorId = 'Please select a doctor';
    }

    if (!formData.appointmentDate) {
      newErrors.appointmentDate = 'Please select an appointment date';
    } else {
      const selectedDate = new Date(formData.appointmentDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        newErrors.appointmentDate = 'Appointment date cannot be in the past';
      }
    }

    if (!formData.appointmentTime) {
      newErrors.appointmentTime = 'Please select an appointment time';
    } else if (bookedTimes.includes(formData.appointmentTime)) {
      newErrors.appointmentTime = 'This time slot is already booked';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const response = await appointmentsApi.createAppointment({
        patientId: formData.patientId,
        doctorId: formData.doctorId,
        appointmentDate: formData.appointmentDate,
        appointmentTime: formData.appointmentTime,
        notes: formData.notes || undefined
      });

      if (response.data.success) {
        toast.success('Appointment created successfully!');
        onSuccess();
        onClose();
      } else {
        toast.error(response.data.message || 'Failed to create appointment');
      }
    } catch (error: any) {
      console.error('Error creating appointment:', error);
      console.error('Error response data:', error.response?.data);
      console.error('Error response status:', error.response?.status);
      console.error('Error response message:', error.response?.data?.message);
      
      if (error.response?.status === 409) {
        toast.error('This time slot is no longer available');
        setErrors({ appointmentTime: 'Time slot is no longer available' });
        checkDoctorAvailability(); // Refresh availability
      } else if (error.response?.status === 400) {
        const errorMessage = error.response?.data?.message || 'Invalid appointment data';
        toast.error(errorMessage);
        setErrors({ appointmentTime: errorMessage });
      } else {
        toast.error('Failed to create appointment');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const getTimeSlotStatus = (time: string) => {
    if (bookedTimes.includes(time)) {
      return 'booked';
    }
    if (!availableTimes.includes(time)) {
      return 'unavailable';
    }
    if (formData.appointmentTime === time) {
      return 'selected';
    }
    return 'available';
  };

  const getTimeSlotClassName = (time: string) => {
    const status = getTimeSlotStatus(time);
    const baseClasses = 'px-3 py-2 text-sm rounded-lg border transition-colors cursor-pointer';
    
    switch (status) {
      case 'booked':
        return `${baseClasses} bg-red-50 text-red-400 border-red-200 cursor-not-allowed`;
      case 'unavailable':
        return `${baseClasses} bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed`;
      case 'selected':
        return `${baseClasses} bg-blue-600 text-white border-blue-600`;
      case 'available':
        return `${baseClasses} bg-white text-gray-700 border-gray-300 hover:border-blue-500 hover:bg-blue-50`;
      default:
        return baseClasses;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <Calendar className="h-6 w-6 mr-2 text-blue-600" />
            Create New Appointment
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {loadingData ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-sm text-gray-500 mt-2">Loading patients and doctors...</p>
            </div>
          ) : (
            <>
              {/* Patient Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <User className="h-4 w-4 inline mr-1" />
                  Patient *
                </label>
                <select
                  value={formData.patientId}
                  onChange={(e) => handleInputChange('patientId', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.patientId ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={!!preSelectedPatientId}
                >
                  <option value="">Select a patient</option>
                  {patients.map((patient) => (
                    <option key={patient.id} value={patient.id}>
                      {patient.user.firstName} {patient.user.lastName} - {patient.patientNumber}
                    </option>
                  ))}
                </select>
                {errors.patientId && (
                  <p className="text-red-500 text-sm mt-1">{errors.patientId}</p>
                )}
              </div>

              {/* Doctor Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <UserCheck className="h-4 w-4 inline mr-1" />
                  Doctor *
                </label>
                <select
                  value={formData.doctorId}
                  onChange={(e) => handleInputChange('doctorId', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.doctorId ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={!!preSelectedDoctorId}
                >
                  <option value="">Select a doctor</option>
                  {doctors.map((doctor) => (
                    <option key={doctor.id} value={doctor.id}>
                      Dr. {doctor.user.firstName} {doctor.user.lastName} - {doctor.specialization}
                    </option>
                  ))}
                </select>
                {errors.doctorId && (
                  <p className="text-red-500 text-sm mt-1">{errors.doctorId}</p>
                )}
              </div>

              {/* Date Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="h-4 w-4 inline mr-1" />
                  Appointment Date *
                </label>
                <input
                  type="date"
                  value={formData.appointmentDate}
                  min={getMinDate()}
                  onChange={(e) => handleInputChange('appointmentDate', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.appointmentDate ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.appointmentDate && (
                  <p className="text-red-500 text-sm mt-1">{errors.appointmentDate}</p>
                )}
              </div>

              {/* Time Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="h-4 w-4 inline mr-1" />
                  Appointment Time *
                </label>
                
                {/* Availability Status */}
                {formData.doctorId && formData.appointmentDate && (
                  <div className="mb-3 p-3 rounded-lg border">
                    {availabilityStatus === 'checking' && (
                      <div className="flex items-center text-blue-600">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                        Checking availability...
                      </div>
                    )}
                    {availabilityStatus === 'available' && availableTimes.length > 0 && (
                      <div className="flex items-center text-green-600">
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Doctor is available on this date
                      </div>
                    )}
                    {availabilityStatus === 'available' && availableTimes.length === 0 && (
                      <div className="flex items-center text-orange-600">
                        <AlertCircle className="h-4 w-4 mr-2" />
                        Doctor has no available time slots on this date
                      </div>
                    )}
                    {availabilityStatus === 'unavailable' && (
                      <div className="flex items-center text-red-600">
                        <AlertCircle className="h-4 w-4 mr-2" />
                        {errors.appointmentTime || 'Unable to check availability'}
                      </div>
                    )}
                  </div>
                )}

                {/* Time Slots Grid */}
                {formData.doctorId && formData.appointmentDate && availabilityStatus === 'available' && availableTimes.length > 0 ? (
                  <div className="grid grid-cols-4 gap-2">
                    {timeSlots.map((time) => {
                      const status = getTimeSlotStatus(time);
                      return (
                        <button
                          key={time}
                          type="button"
                          onClick={() => status === 'available' ? handleInputChange('appointmentTime', time) : null}
                          className={getTimeSlotClassName(time)}
                          disabled={status === 'booked' || status === 'unavailable'}
                          title={
                            status === 'booked' 
                              ? 'This time slot is already booked' 
                              : status === 'unavailable'
                              ? 'Doctor is not available at this time'
                              : ''
                          }
                        >
                          {time}
                          {status === 'booked' && (
                            <span className="block text-xs">Booked</span>
                          )}
                          {status === 'unavailable' && (
                            <span className="block text-xs">Unavailable</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ) : formData.doctorId && formData.appointmentDate && availabilityStatus === 'available' && availableTimes.length === 0 ? (
                  <p className="text-orange-600 text-sm py-4 text-center border border-orange-200 rounded-lg bg-orange-50">
                    No available time slots for this date. Please select a different date or contact the doctor to update their schedule.
                  </p>
                ) : formData.doctorId && formData.appointmentDate && availabilityStatus === 'unavailable' ? (
                  <p className="text-red-600 text-sm py-4 text-center border border-red-200 rounded-lg bg-red-50">
                    {errors.appointmentTime || 'Unable to check doctor availability. Please try again.'}
                  </p>
                ) : (
                  <p className="text-gray-500 text-sm py-4 text-center border border-gray-200 rounded-lg">
                    Please select a doctor and date to view available time slots
                  </p>
                )}

                {errors.appointmentTime && (
                  <p className="text-red-500 text-sm mt-1">{errors.appointmentTime}</p>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Any additional notes for the appointment..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </>
          )}

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              disabled={loading || loadingData}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating...
                </>
              ) : (
                <>
                  <Calendar className="h-4 w-4 mr-2" />
                  Create Appointment
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateAppointmentModal; 